import { EventStatus } from '../../../interfaces/events';
import {
  Balance,
  HeadquarterBalance,
} from '../../../interfaces/strapiComponents/balance';
import {
  getEstimatedHeadquarterIncome,
  getEstimatedHeadquarterOutflow,
  getEstimatedIncome,
  getEstimatedOutflow,
} from '../../../libs/utils/balancesAggretator';

export const DEFAULT_GLASSES_PER_BOTTLE = 20;
export const MAX_GLASSES_PER_BOTTLE = 20;

export type EventPublishValidationCode =
  | 'NEGATIVE_ESTIMATED_MARGIN'
  | 'ZERO_PRICE_WITH_NEGATIVE_BALANCE';

export type EventPublishValidationResult =
  | { isValid: true }
  | {
      isValid: false;
      code: EventPublishValidationCode;
    };

export const hasExplicitNumber = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  return Number.isFinite(Number(value));
};

export const resolveExplicitNumber = (
  value: unknown,
  fallback: unknown = 0,
): number => {
  if (hasExplicitNumber(value)) {
    return Number(value);
  }

  if (hasExplicitNumber(fallback)) {
    return Number(fallback);
  }

  return 0;
};

export const normalizeGlassesPerBottle = (value: unknown): number => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return DEFAULT_GLASSES_PER_BOTTLE;
  }

  return Math.min(Math.round(parsedValue), MAX_GLASSES_PER_BOTTLE);
};

export const calculateRequiredBottles = (
  participants: unknown,
  glassesPerBottle: unknown,
): number => {
  const normalizedParticipants = Number(participants);

  if (!Number.isFinite(normalizedParticipants) || normalizedParticipants <= 0) {
    return 0;
  }

  return Math.ceil(
    normalizedParticipants / normalizeGlassesPerBottle(glassesPerBottle),
  );
};

export const getDisplayedEstimatedMargin = (
  balance: Balance,
  headquarterBalance: HeadquarterBalance,
) => {
  const estimatedIncome = getEstimatedIncome(balance);
  const estimatedOutflow = getEstimatedOutflow(balance);
  const estimatedHeadquarterOutflow =
    getEstimatedHeadquarterOutflow(headquarterBalance);

  return estimatedIncome - estimatedOutflow - estimatedHeadquarterOutflow;
};

export const getOverallEstimatedTotals = (
  balance: Balance,
  headquarterBalance: HeadquarterBalance,
) => {
  const estimatedIncomeOverall =
    getEstimatedIncome(balance) + getEstimatedHeadquarterIncome(headquarterBalance);
  const estimatedOutflowOverall =
    getEstimatedOutflow(balance) +
    getEstimatedHeadquarterOutflow(headquarterBalance);

  return {
    estimatedIncomeOverall,
    estimatedOutflowOverall,
  };
};

export const validateEventPublish = ({
  subscriptionAmount,
  balance,
  headquarterBalance,
  allowNegativeEstimatedMargin = false,
}: {
  subscriptionAmount: unknown;
  balance: Balance;
  headquarterBalance: HeadquarterBalance;
  allowNegativeEstimatedMargin?: boolean;
}): EventPublishValidationResult => {
  if (
    !allowNegativeEstimatedMargin &&
    getDisplayedEstimatedMargin(balance, headquarterBalance) < 0
  ) {
    return {
      isValid: false,
      code: 'NEGATIVE_ESTIMATED_MARGIN',
    };
  }

  const normalizedSubscriptionAmount = resolveExplicitNumber(subscriptionAmount);

  if (!allowNegativeEstimatedMargin && normalizedSubscriptionAmount === 0) {
    const { estimatedIncomeOverall, estimatedOutflowOverall } =
      getOverallEstimatedTotals(balance, headquarterBalance);

    if (estimatedOutflowOverall > estimatedIncomeOverall) {
      return {
        isValid: false,
        code: 'ZERO_PRICE_WITH_NEGATIVE_BALANCE',
      };
    }
  }

  return { isValid: true };
};

export const isPublishAction = (status?: EventStatus) =>
  status === EventStatus.PUBLISHED;
